/**
 * WHERE A PERSON LANDS AFTER SIGNING IN — decided in one place.
 *
 * IT USED TO BE DECIDED IN THREE, and they did not agree. `LoginPage` had the
 * default for a normal sign-in, `LoginPage` had a second copy in its
 * never-bounce-back-to-/login guard, and `App.tsx`'s `DefaultLanding` had a
 * third for anyone who hit `/` while already signed in. Only the third knew
 * that a Transport Manager must not be sent to a dashboard, so the rule was
 * right in one of the three places it was written.
 *
 * A normal sign-in never reaches `DefaultLanding` — it redirects straight from
 * the login page — so changing the obvious one would have left the `/` case
 * behind, which is the one a person hits by clicking the logo.
 *
 * Reagan, 2026-10-03: the landing page should be the Dashboard.
 */
import { getCurrentUserRole } from './auth';

/**
 * TRANSPORT MANAGERS ARE FLEET-SCOPED and have neither dashboard nor guidance
 * access (App.tsx, 2026-06-06). Sending them to the Dashboard would put them
 * on a screen their role is not for — the one carve-out that has to survive
 * this change, and the one the login page never had.
 */
const FLEET_ONLY_ROLES = new Set(['transport_manager']);

/**
 * The route to land on after a successful sign-in, or when an already
 * signed-in person arrives at `/`.
 *
 * It takes no account of a person's granted sections, deliberately: the
 * Dashboard is reachable for every role that is not carved out above —
 * checked on screen as the seeded `accounts` user, whose `allowed_sections`
 * holds only `pharmacy` and who still renders a full dashboard. If that ever
 * stops being true, this is the function that has to learn about it, rather
 * than each caller finding out separately.
 */
export function landingRoute(): string {
  return FLEET_ONLY_ROLES.has(getCurrentUserRole() ?? '') ? '/fleet' : '/dashboard';
}
